import { PDFOptions } from 'puppeteer-core';
import BwipJs from 'bwip-js';
import { IGerarPDFSicoob } from './types';
import maskFormat from '@/utils/maskFormat';
import { compileHbs } from '@/utils/compileHbs';
import { getPathTemplates } from '@/utils/getPathTemplates';
import { api } from '@/services/api';
import StringMask from 'string-mask';
import { decompress } from '@/utils/zip';

export async function gerarPDF({ dados }: IGerarPDFSicoob) {
  try {
    const { dadosPDF, generatePDF } = dados;

    if (!dadosPDF.pagador.cpfcnpj) {
      throw new Error('CPF/CNPJ do pagador não informado!');
    }

    const codigoBarra = await BwipJs.toBuffer({
      bcid: 'interleaved2of5',
      text: dadosPDF.boleto.codigoBarra,
      height: 20,
    });

    dadosPDF.beneficiario.dadosBancario.agencia = formatAgenciaCodigoCedente(
      dadosPDF.beneficiario.dadosBancario.agencia,
      dadosPDF.beneficiario.dadosBancario.codigo_cliente,
    );

    dadosPDF.beneficiario.dadosBancario.carteira = dadosPDF.beneficiario.dadosBancario.carteira.padStart(2, '0');

    dadosPDF.boleto.nossoNumero = formatNossoNumero(
      dadosPDF.boleto.parcela,
      dadosPDF.boleto.nossoNumero,
    );

    dadosPDF.pagador.cpfcnpj =
      dadosPDF.pagador.cpfcnpj.length === 11 ?
      `CPF: ${maskFormat.retornaCpfCnpjFormatado(dadosPDF.pagador.cpfcnpj)}` :
      `CNPJ: ${maskFormat.retornaCpfCnpjFormatado(dadosPDF.pagador.cpfcnpj)}`;

    dadosPDF.boleto.linhaDigitavel = maskFormat.codigoBarra(dadosPDF.boleto.linhaDigitavel);
    dadosPDF.boleto.codigoBarra = maskFormat.codigoBarra(dadosPDF.boleto.codigoBarra);
    dadosPDF.boleto.codigoBarraImage = codigoBarra.toString('base64');

    dadosPDF.pagador.endereco.cep = maskFormat.retornaCEPFormatado(
      dadosPDF.pagador.endereco.cep,
    );

    dadosPDF.boleto.valorAcrescimo = maskFormat.numberToReal(
      dadosPDF.boleto.valorAcrescimo,
    );

    dadosPDF.boleto.valorCobrado = maskFormat.numberToReal(
      dadosPDF.boleto.valorCobrado,
    );

    dadosPDF.boleto.valorDescontoAbatimento = maskFormat.numberToReal(
      dadosPDF.boleto.valorDescontoAbatimento,
    );

    const valorDocumento = Number(dadosPDF.boleto.valorDocumento);
    dadosPDF.boleto.valorDocumento = maskFormat.numberToReal(
      dadosPDF.boleto.valorDocumento,
    );

    dadosPDF.boleto.valorMulta = maskFormat.numberToReal(dadosPDF.boleto.valorMulta);

    dadosPDF.boleto.valorOutrasDeducoes = maskFormat.numberToReal(
      dadosPDF.boleto.valorOutrasDeducoes,
    );

    if (dadosPDF.boleto.quantidade) {
      dadosPDF.boleto.quantidade = maskFormat.numberToReal(dadosPDF.boleto.quantidade);
    }

    const jurosMes = dadosPDF.beneficiario.dadosBancario.juros;
    const jurosDia = (jurosMes / 100 / 30) * valorDocumento;
    if (jurosDia > 0) {
      dadosPDF.boleto.instrucao1 = `COBRAR JUROS DE R$${maskFormat.numberToReal(jurosDia)} AO DIA, APÓS O VENCIMENTO`;
    }

    const descontoVencimento = dadosPDF.beneficiario.dadosBancario.desconto || 0;
    if (descontoVencimento > 0) {
      dadosPDF.boleto.instrucao2 = `ATÉ O VENCIMENTO, CONCEDER DESCONTO DE ${descontoVencimento}%`;
    }

    const multaAtraso = dadosPDF.beneficiario.dadosBancario.multa || 0;
    if (multaAtraso > 0) {
      dadosPDF.boleto.instrucao3 = `MULTA DE ${multaAtraso}%`;
    }

    const protestoDias = dadosPDF.beneficiario.dadosBancario.protesto || 0;
    if (protestoDias > 0) {
      dadosPDF.boleto.instrucao4 = `PROTESTAR APÓS ${protestoDias} DIAS CORRIDOS DO VENCIMENTO`;
    }

    dadosPDF.boleto.valor = maskFormat.numberToReal(dadosPDF.boleto.valor);
    dadosPDF.pagador.isCnpj = dadosPDF.pagador.cpfcnpj.length > 14;
    const content = compileHbs(getPathTemplates('sicoob-boleto.hbs'), dadosPDF);

    const config: PDFOptions = {
      displayHeaderFooter: false,
      path: './boleto.pdf',
      format: 'a4',
      printBackground: true,
    };

    let pdfBase64: string | null = null;

    if (generatePDF) {
      pdfBase64 = await generatePDF(content, config);
    } else {
      if (!api.defaults.baseURL) {
        throw new Error('BaseURL api not found');
      }

      const response = await api.post('/', { content, config });
      const buffer = Buffer.from(response.data.pdf);
      pdfBase64 = await decompress(buffer);
    }

    return {
      erro: false,
      dados: pdfBase64,
    };
  } catch (error) {
    console.log(error);
    if (error instanceof Error) {
      return {
        erro: true,
        dados: error.message,
      }
    }
    return {
      erro: true,
      dados: 'Falha ao gerar impressão do boleto',
    };
  }
}

function formatAgenciaCodigoCedente(agencia: string, conta: string) {
  agencia = agencia.slice(0, -1).padStart(4, '0');
  conta = conta.padStart(7, '0');

  const agenciaFmt = new StringMask('0000.000000-0');
  return agenciaFmt.apply(agencia + conta);
}

function formatNossoNumero(parcela: number | string, nossoNumero: string) {
  parcela = String(parcela).padStart(2, '0');
  nossoNumero = nossoNumero.padStart(8, '0');
  return `${parcela}/${nossoNumero.slice(0, 7)}-${nossoNumero.slice(7, 8)}`;
}
